import type {
    ApplePayConfiguration,
    DropinConfiguration,
    GooglePayConfiguration,
} from "@adyen/adyen-web";
import type { TPaymentMethods } from "@features/checkout/types";
import { EcontextPaymentMethod, paymentMethodTypes } from "@lib/constants/payment";
import { loadPaymentMethodComponents } from "../../../../../../lib/utils/checkout-utility/adyen/loadPaymentMethodComponents";
import typography from "@ecco/ui/tokens/typography";
import colors from "@ecco/ui/tokens/colors";
type GetBaseConfigurationParams = {
    paymentMethods: TPaymentMethods[];
};
export const getBaseConfiguration = async ({
    paymentMethods,
}: GetBaseConfigurationParams): Promise<Partial<DropinConfiguration>> => {
    const applePayConfig = (paymentMethods?.find(
        (method) => method.type === paymentMethodTypes.ApplePay
    )?._Configuration ?? {
        merchantId: "",
        merchantName: "",
    }) as ApplePayConfiguration["configuration"];
    const googlePayConfig = (paymentMethods?.find(
        (method) => method.type === paymentMethodTypes.GooglePay
    )?._Configuration ?? {
        gatewayMerchantId: "",
        merchantId: "",
    }) as GooglePayConfiguration["configuration"];

    // Load components based on available payment methods before creating Dropin config
    const paymentMethodComponents = await loadPaymentMethodComponents(paymentMethods || []);

    // A width breakpoint puts iPad portrait (768px) on the desktop branch, where the
    // smaller font makes iOS Safari zoom on focus. Pointer capability is the real signal.
    const isTouchDevice = window.matchMedia("(hover: none) and (pointer: coarse)").matches;

    // Some styles for payment component we need to configure here before creating the component.
    // For example fontFamily, fontSize, etc. We can't override it in the css file.
    const paymentMethodStyles = {
        base: {
            fontFamily: typography.fonts.body,
            fontSmoothing: "antialiased",
            webkitFontSmoothing: "antialiased",
            // Secure fields live in Adyen iframes, so we cannot scale them down the way
            // the Input sizes do. Touch devices get a full 16px to avoid the iOS zoom.
            fontSize: isTouchDevice ? "16px" : "14px",
            fontWeight: "bold",
            padding: "14px 20px",
        },
        placeholder: {
            color: colors.gray[200],
            fontWeight: `${typography.fontWeights.normal}`,
        },
    };

    const baseConfiguration: Partial<DropinConfiguration> = {
        showRadioButton: true,
        paymentMethodsConfiguration: {
            card: {
                styles: paymentMethodStyles,
                holderNameRequired: true,
                hasHolderName: true,
                maskSecurityCode: true,
            },
            [EcontextPaymentMethod.Stores]: {
                personalDetailsRequired: false,
            },
            [EcontextPaymentMethod.SevenEleven]: {
                personalDetailsRequired: false,
            },
            applepay: {
                configuration: applePayConfig,
            },
            googlepay: {
                configuration: googlePayConfig,
            },
        },
        // Allows us to call actions.resolve or actions.reject without showing the default Adyen animation screen.
        disableFinalAnimation: true,
        paymentMethodComponents: paymentMethodComponents ?? undefined,
    };

    return baseConfiguration;
};
